'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import { label, type Labels } from './labels';
import type { LightboxImage } from './Lightbox';

// The viewer is fetched on the first press (or hover/focus, just before it), never with the page.
const Lightbox = dynamic(() => import('./Lightbox'), { ssr: false });
const preloadLightbox = () => {
  void import('./Lightbox');
};

/**
 * The product photographs: the hero and a row of thumbnails, each a button that opens the viewer
 * at that image.
 *
 * <p>The hero is still an `<img>` in the server-rendered HTML with `loading="eager"` and
 * `fetchPriority="high"` — it is the page's LCP element and must not wait for any JavaScript.
 * Making it a button changes nothing about when it loads, only what pressing it does.
 *
 * <p>Each button's name is "Enlarge image n of m" followed by the photograph's alt text, so a
 * screen-reader user hears both what the button does and what the picture is. A thumbnail's alt
 * is kept for the same reason rather than emptied as decorative.
 */
export function ProductGallery({
  images,
  heroClassName,
  galleryLabel,
  labels,
}: {
  /** Hero first. */
  images: LightboxImage[];
  heroClassName: string;
  /** The thumbnails list's name ("Product images"). */
  galleryLabel: string;
  labels: Labels;
}) {
  const [openAt, setOpenAt] = useState<number | null>(null);
  const hero = images[0];
  if (!hero) return null;
  const total = images.length;
  const enlarge = (index: number) => label(labels, 'media.gallery.enlarge', { n: index + 1, total });
  const buttonClass =
    'block w-full cursor-zoom-in focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-border-focus)]';

  return (
    <>
      <button
        type="button"
        data-gallery-hero
        className={buttonClass}
        onClick={() => setOpenAt(0)}
        onPointerEnter={preloadLightbox}
        onFocus={preloadLightbox}
      >
        <span className="sr-only">{enlarge(0)}</span>
        {/* eslint-disable-next-line @next/next/no-img-element -- per-tenant
            asset host, not knowable at build time for next/image's allowlist. */}
        <img
          src={hero.url}
          // The gallery is half the page from lg up, the full width below it.
          {...(hero.srcSet ? { srcSet: hero.srcSet, sizes: '(min-width: 1024px) 50vw, 100vw' } : {})}
          alt={hero.alt}
          // No width/height attributes: the box's aspect ratio is fixed by the class already,
          // and a height attribute would override it as a presentational hint.
          className={heroClassName}
          // The hero is the LCP element on this page: eager, high priority, and never lazy.
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      </button>

      {total > 1 ? (
        <ul aria-label={galleryLabel} className="mt-4 grid grid-cols-4 gap-2">
          {images.slice(1).map((image, offset) => (
            <li key={image.url}>
              <button
                type="button"
                data-gallery-thumb
                className={`${buttonClass} rounded-md`}
                onClick={() => setOpenAt(offset + 1)}
                onPointerEnter={preloadLightbox}
                onFocus={preloadLightbox}
              >
                <span className="sr-only">{enlarge(offset + 1)}</span>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={image.url}
                  // Four to a row in the gallery column.
                  {...(image.srcSet
                    ? { srcSet: image.srcSet, sizes: '(min-width: 1024px) 12vw, 25vw' }
                    : {})}
                  alt={image.alt}
                  className="aspect-square w-full rounded-md border border-[var(--color-border-subtle)] object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {openAt != null ? (
        <Lightbox images={images} startIndex={openAt} labels={labels} onClose={() => setOpenAt(null)} />
      ) : null}
    </>
  );
}
